import { addDays, differenceInDays, endOfWeek, formatISO, lastDayOfYear, parseISO, startOfWeek } from 'date-fns';
import { ReactElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
// import { renderToStaticMarkup } from 'react-dom/server';
import { EValidationTestType } from './validationConfigs';

/**
 * Renders the appropriate content depending if the predicate is true or false
 */
export function renderIf(
  condition: boolean,
  content: {
    ifTrue: () => JSX.Element | JSX.Element[];
    ifFalse: () => JSX.Element | JSX.Element[];
  },
): JSX.Element | JSX.Element[] {
  if (condition) {
    return content.ifTrue();
  } else {
    return content.ifFalse();
  }
}

export function inputIsRequired(validationTests) {
  return (
    validationTests.indexOf(EValidationTestType.hasValue) >= 0 ||
    validationTests.indexOf(EValidationTestType.hasAnyValue) >= 0
  );
}

/**
 * Renders the given content only if the condition is true
 */
export function renderIfTrue(condition: boolean, content: () => JSX.Element): JSX.Element | JSX.Element[] | null {
  if (condition) {
    return content();
  }
  return null;
}

/**
 * Renders the given content only if the condition is false
 */
export function renderIfFalse(
  condition: boolean,
  content: () => JSX.Element | JSX.Element[],
): JSX.Element | JSX.Element[] | null {
  if (!condition) {
    return content();
  }
  return null;
}

/**
 * Renders the appropriate content depending if the variable is defined or not
 * @param data      Data variable to check if is defined & not null
 * @param content   The content to render
 */
export function renderDefinedTrue<T>(data: T, content: (data: T) => JSX.Element): JSX.Element | null {
  if (typeof data !== 'undefined' && data !== null) {
    return content(data);
  } else {
    return null;
  }
}

/**
 * Renders the appropriate content depending if the variable is defined or not
 * @param data      Data variable to check if is defined & not null
 * @param content   The content to render
 */
export function renderDefined<T>(
  data: T,
  content: {
    isDefined: (data: T) => JSX.Element | JSX.Element[];
    isNotDefined: () => JSX.Element | JSX.Element[];
  },
): JSX.Element | JSX.Element[] {
  if (typeof data !== 'undefined' && data !== null) {
    return content.isDefined && content.isDefined(data);
  } else {
    return content.isNotDefined && content.isNotDefined();
  }
}

/**
 * Renders a callback from the callbacks dictionary based on a key (similar to the switch statement)
 * @param key               Key of the callback to call
 * @param callbacks         All the available callbacks
 * @param defaultCallback   Available if nothing else matches
 */
export function renderSwitch(
  key: string | number,
  callbacks: IDictionary<() => JSX.Element>,
  defaultCallback?: () => JSX.Element,
): JSX.Element | null {
  if (callbacks[key]) {
    return callbacks[key]();
  } else {
    if (defaultCallback) {
      return defaultCallback();
    } else {
      return null;
    }
  }
}

export const parseDate = (str, type?) => {
  if (/\d{4}\-\d{2}\-\d{2}/gm.test(str)) {
    if (str != null && str != '') {
      var d = new Date(str),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear(),
        hours = '' + d.getHours(),
        minutes = '' + d.getMinutes();

      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;
      if (hours.length < 2) hours = '0' + hours;
      if (minutes.length < 2) minutes = '0' + minutes;

      if (type && type == 'hr-min') {
        return [day, month, year].join('.') + ' - ' + [hours, minutes].join(':');
      }
      const date = [day, month, year].join('.');
      if (date.includes('NaN')) {
        return;
      }
      return date;
    } else {
      return '';
    }
  } else {
    return str;
  }
};

export const parseToApiDate = (dateString, type?) => {
  return new Date(formatISO(new Date(dateString), { representation: 'date' })).toISOString();
};

export function removeEmptyObjects(o) {
  for (var k in o) {
    if (!o[k] || typeof o[k] !== 'object') {
      continue; // If null or not an object, skip to the next iteration
    }

    // The property is an object
    removeEmptyObjects(o[k]); // <-- Make a recursive call on the nested object
    if (Object.keys(o[k]).length === 0) {
      delete o[k]; // The object had no properties, so delete that property
    }
  }
  return o;
}

export function emptyStringsToNull(o) {
  for (var k in o) {
    if (!o[k] || typeof o[k] !== 'object') {
      if (o[k] === '') {
        o[k] = null;
      }
      // undefined should be removed
      if (typeof o[k] === 'undefined') {
        delete o[k];
      }
    } else {
      emptyStringsToNull(o[k]);
    }
  }
  return o;
}

export function toLowerCaseKeys(obj) {
  var key,
    keys = Object.keys(obj);
  var n = keys.length;
  var newobj = {};
  while (n--) {
    key = keys[n];
    newobj[key.toLowerCase()] = obj[key];
  }
  return newobj;
}

export function objectsToArray(obj) {
  let array = [];
  Object.values(obj).forEach((item) => {
    array.push(item);
  });
  return array;
}

export function formatNumber(num, trailingDigits?) {
  if (!num) {
    return num;
  }
  if (typeof num == 'string' && /[a-zA-Z]/.test(num)) {
    return num;
  }
  if (typeof num == 'string') {
    num = num.replace(/'/g, '').replace(/,/g, '.');
  }
  return parseFloat(num)
    .toFixed(trailingDigits || 0)
    .replace(/./g, function (c, i, a) {
      return i > 0 && c !== '.' && (a.length - i) % 3 === 0 ? ' ' + c : c;
    });
}

export function addToLSObject(lsObjName, attr, val) {
  const lsObj = JSON.parse((window as any).localStorage.getItem(lsObjName)) || {};

  lsObj[attr] = val;

  const stringified = JSON.stringify(lsObj);
  try {
    (window as any).localStorage.setItem(lsObjName, stringified);
  } catch (error) {
    console.warn('Error when adding to LS', error);
    return false;
  }
}

export function addToSSObject(lsObjName, attr, val) {
  try {
    const lsObj = JSON.parse((window as any).sessionStorage.getItem(lsObjName)) || {};

    lsObj[attr] = val;
    lsObj['commitHash'] = (window as any).commitHash;
    lsObj['clientHash'] = (window as any).clientHash;

    const stringified = JSON.stringify(lsObj);
    (window as any).sessionStorage.setItem(lsObjName, stringified);
  } catch (error) {
    console.warn('Error when saving to SS', error);
    return false;
  }
}

export function saveToSSObject(lsObjName, attr, val) {
  const stringified = JSON.stringify({ [attr]: val });
  try {
    (window as any).sessionStorage.setItem(lsObjName, stringified);
  } catch (error) {
    console.warn('Error when saving to SS', error);
    return false;
  }
}

export const saveFullObjToSSObject = (ssObjName: string, val) => {
  try {
    (window as any).sessionStorage.setItem(ssObjName, JSON.stringify(val));
  } catch (error) {
    console.warn('Error when saving to SS', error);
    return false;
  }
};

export const getDataFromSSObject = (ssObjName: string) => {
  try {
    return JSON.parse((window as any).sessionStorage.getItem(ssObjName));
  } catch (error) {
    console.warn('Error when getting data from SS', error);
    return false;
  }
};

export function reverseGeocode(latLang) {
  var geocoder = new (window as any).google.maps.Geocoder();
  var OK = (window as any).google.maps.GeocoderStatus.OK;

  return new Promise(function (resolve, reject) {
    geocoder.geocode({ location: latLang }, function (results, status) {
      if (status !== OK) {
        reject(status);
      }
      resolve(results);
    });
  });
}

export function replaceUmlauts(text) {
  return String(escape(text));
}

export const flattenMessages = (nestedMessages, prefix = '') => {
  if (nestedMessages === null) {
    return {};
  }
  return Object.keys(nestedMessages).reduce((messages, key) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      Object.assign(messages, { [prefixedKey]: value });
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }

    return messages;
  }, {});
};

export const parseJwt = (token) => {
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const createDateRange = (date, days, options?) => {
  let startDate = typeof date === 'string' ? parseISO(date) : date;
  let daysToAdd = days;
  let key = 'selection';

  if (options && options.fullWeeks == true) {
    startDate = startOfWeek(startDate, { weekStartsOn: 1 });
  }

  if (options && options.key) {
    key = options.key;
  }

  let endDate = addDays(startDate, daysToAdd - 1);

  if (options && options.fullWeeks == true) {
    endDate = endOfWeek(endDate, { weekStartsOn: 1 });
  } else {
    endDate = addDays(startDate, days - 1);
  }

  if (options && options.toEndOfYear) {
    const lastDateOfYear = lastDayOfYear(startDate);
    const daysToEndOfYear = differenceInDays(startDate, lastDateOfYear);
    const numberOfDaysToEndOfYear = Math.abs(daysToEndOfYear);
    endDate = addDays(startDate, numberOfDaysToEndOfYear);
  }

  let range = {
    startDate,
    endDate,
    key: key,
    autofocus: true,
    showDateDisplay: true,
  };

  if (options && options.key && options.key !== 'selection') {
    range.autofocus = false;
    range.showDateDisplay = false;
  }

  return range;
};

export const prepareCoordinates = (data) => {
  return Object.keys(data).map((key) => {
    let mode = 1;
    let name = data[key].address;
    if (data[key].types && data[key].types.includes('locality')) {
      mode = 0;
    }
    if ((data[key].types && data[key].types.includes('postal_code')) || data[key].context === 'postal_code') {
      mode = 2;
      name = name.split(' ')[0];
    }
    return {
      name: name,
      latitude: data[key].lngLtd?.lat || data[key].latitude,
      longitude: data[key].lngLtd?.lng || data[key].longitude,
      radius: data[key].circle?.radius,
      mode: mode,
    };
  });
};

export const prepareFormatFilters = (formats) => {
  if (!formats) return [];
  if (!formats.length) return [];
  formats = formats.filter((format) => format.group === 'classic');
  return formats.map((format) => {
    return {
      title: format.value,
      text: '',
      value: format.id,
      disabled: format.state,
    };
  });
};

export const prepareTrafficFilters = (formats) => {
  if (!formats) return [];
  if (!formats.length) return [];
  let traffic = formats.filter((format) => format.group === 'traffic');
  return traffic.map((tr) => {
    return {
      title: tr.value,
      text: '',
      value: tr.id,
      disabled: tr.state,
    };
  });
};

export const prepareDigitalFilters = (formats) => {
  if (!formats) return [];
  if (!formats.length) return [];
  let digital = formats.filter((format) => format.group === 'digital');
  return digital.map((di) => {
    return {
      title: di.value,
      text: '',
      value: di.id,
      disabled: di.state,
    };
  });
};

export const prepareTargetFilters = (target) => {
  if (!target) return [];
  if (!target.length) return [];
  return target.map((di) => {
    return {
      title: di.value,
      text: '',
      value: di.id,
      disabled: di.state,
    };
  });
};

export const addOrRemoveFromArray = (state, value, array) => {
  const selected = [...array];
  if (state) {
    if (!selected.includes(value)) {
      selected.push(value);
    }
  } else {
    if (selected.includes(value)) {
      const index = selected.indexOf(value);
      selected.splice(index, 1);
    }
  }
  return selected;
};

export const isComingFromJ1 = (urlHistory) => {
  const lastEntry = urlHistory[urlHistory.length - 2] || '';
  const beforeLastEntry = urlHistory[urlHistory.length - 3] || '';
  return !!(lastEntry.includes('journey1') || beforeLastEntry.includes('journey1'));
};

export const isComingFromJ2 = (urlHistory) => {
  const lastEntry = urlHistory[urlHistory.length - 2] || '';
  const beforeLastEntry = urlHistory[urlHistory.length - 3] || '';
  if (lastEntry.includes('journey2') || beforeLastEntry.includes('journey2')) {
    return true;
  } else {
    return false;
  }
};

/**
 * Encode svg image in UTF-8 format;
 */
export const encodeSvg = (el: ReactElement) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(renderToStaticMarkup(el))}`;

export const scrollToTop = () => {
  if (process.env.REACT_APP_MODE === 'django') {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  } else {
    document.getElementById('root').scrollTo({ top: 0, behavior: 'smooth' });
  }
};
